import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import { apiService } from '../services/api';
import { FiSave, FiEdit2, FiX, FiCheck, FiSettings } from 'react-icons/fi';

interface MasterVariable {
  id: number;
  name: string;
  value: number;
  // Text-valued variables live here instead of in `value`, which is a decimal
  // column. Which one a variable uses is fixed by its kind below.
  string_value: string;
  updated_at: string;
}

type VariableKind = 'number' | 'boolean' | 'text';

// Variable metadata: unit label, description, and - where it is not a plain
// number - the kind of value the variable holds.
const VARIABLE_META: Record<string, { unit: string; description: string; kind?: VariableKind }> = {
  SendEmailBeforeCreditLimit: {
    unit: 'credits',
    description: 'Send warning email when remaining AI credits ≤ this value',
  },
  SendEmailBeforeStorageLimit: {
    unit: 'Mo',
    description: 'Send warning email when remaining storage ≤ this value (in Mo)',
  },
  SendEmailPackageExpireReminder: {
    unit: 'days',
    description: 'Send reminder email when package expires within this many days',
  },
  DeleteDataAfterExpiry: {
    unit: 'days',
    description: 'Delete user data this many days after package expiry',
  },
  PriceInputToken: {
    unit: '$ per M',
    description: 'Input token price per million tokens in USD (e.g., 0.075 = $0.075 per million)',
  },
  PriceOutputToken: {
    unit: '$ per M',
    description: 'Output token price per million tokens in USD (e.g., 0.30 = $0.30 per million)',
  },
  AiSkillsMax: {
    unit: 'skills',
    description: 'Maximum number of skills returned by AI autofill when creating jobs (minimum: 5)',
  },
  AffiliationPercentage: {
    unit: '%',
    description: 'Percentage of referred user payments earned by affiliate (e.g., 5.0 = 5%)',
  },
  GuestUserMaxCredits: {
    unit: 'credits',
    description: 'Maximum credits allowed for guest users from Chrome extension (minimum: 1)',
  },
  GuestCreditsResetDuration: {
    unit: 'days',
    description: 'Reset guest credits after this many days (1=daily, 7=weekly, 30=monthly, 90=quarterly)',
  },
  TalentFinderProfilesPerPack: {
    unit: 'profiles',
    description: 'Profiles delivered per Talent Finder pack. One pack = one LinkedIn search page, which is billed at a flat rate whether we take 1 profile or all 25 — so 25 keeps cost and delivery aligned.',
  },
  TalentFinderPackPricing: {
    unit: 'credits',
    description: 'Credits charged per Talent Finder pack (e.g., 20 = 20 credits for a pack of 25 profiles). Packs are charged whole and refunded whole.',
  },
  TalentFinderMinTalents: {
    unit: 'talents',
    description: 'Fewest talents a user may request in one Talent Finder search (slider floor). Should be a multiple of ProfilesPerPack.',
  },
  PublicProfile: {
    kind: 'boolean',
    unit: '',
    description: 'Turns the public-profile lookup on or off across the app. When disabled, the "Public profiles" action disappears from the CV list in Job Details and the public-profile block is hidden in CV Details. Profiles already found stay in the database and reappear if it is switched back on.',
  },
  ChromeExtensionredirectionLink: {
    kind: 'text',
    unit: '',
    description: 'URL the Chrome extension redirects to. Reserved for an upcoming release - nothing reads it yet, so leaving it empty changes nothing.',
  },
  TalentFinderMaxTalents: {
    unit: 'talents',
    description: 'Most talents a user may request in one Talent Finder search (slider ceiling, stepped by ProfilesPerPack). Larger values queue more CV analyses — the pipeline runs ~40 concurrent Gemini calls, so very high ceilings can be slow.',
  },
};

const getVariableMeta = (name: string) => {
  const meta = VARIABLE_META[name] || { unit: '', description: '' };
  return { kind: 'number' as VariableKind, ...meta };
};

const MasterVariables: React.FC = () => {
  const [variables, setVariables] = useState<MasterVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('0');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    fetchVariables();
  }, []);

  const fetchVariables = async () => {
    setLoading(true);
    try {
      const response = await apiService.getMasterVariables();
      if (response.success && response.data) {
        setVariables(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to load master variables', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (variable: MasterVariable) => {
    const { kind } = getVariableMeta(variable.name);
    setEditingId(variable.id);
    setEditValue(
      kind === 'text'
        ? (variable.string_value || '')
        : String(Number(variable.value))
    );
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('0');
  };

  const handleSave = async (id: number) => {
    setSaving(true);
    try {
      const variable = variables.find((v) => v.id === id);
      const kind = variable ? getVariableMeta(variable.name).kind : 'number';
      // A text variable leaves the decimal column alone and vice versa, so a
      // save never writes a meaningless 0 into the column it does not use.
      const payload =
        kind === 'text'
          ? { string_value: editValue }
          : { value: parseFloat(editValue) || 0 };
      const response = await apiService.updateMasterVariable(id, payload);
      if (response.success) {
        setToast({ message: 'Variable updated successfully', type: 'success' });
        setEditingId(null);
        fetchVariables();
      } else {
        const err = response as any;
        setToast({ message: err.error?.message || 'Failed to update variable', type: 'error' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to update variable', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatVariableName = (name: string): string => {
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Master Variables</h1>
          </div>
        </div>

        {/* Variables List */}
        <div className="rounded-xl bg-white shadow-lg dark:bg-navy-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <div className="col-span-5">Variable Name</div>
              <div className="col-span-3 text-center">Value</div>
              <div className="col-span-3 text-center">Last Updated</div>
              <div className="col-span-1 text-center">Actions</div>
            </div>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {variables.length === 0 && (
              <div className="px-6 py-12 text-center">
                <FiSettings className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 text-gray-500 dark:text-gray-400">No master variables found.</p>
              </div>
            )}
            {variables.map((variable) => {
              const meta = getVariableMeta(variable.name);
              return (
                <div
                  key={variable.id}
                  className="grid grid-cols-12 items-center gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-navy-700 transition"
                >
                  {/* Name */}
                  <div className="col-span-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
                        <FiSettings className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{formatVariableName(variable.name)}</p>
                        <p className="text-xs text-gray-400 font-mono">{variable.name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Value */}
                  <div className="col-span-3 text-center">
                    {editingId === variable.id ? (
                      <div className="flex items-center justify-center gap-1">
                        {meta.kind === 'boolean' ? (
                          <select
                            value={editValue === '1' ? '1' : '0'}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="rounded-lg border border-brand-300 px-3 py-1.5 text-sm font-semibold focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-brand-600 dark:bg-navy-700 dark:text-white"
                            autoFocus
                          >
                            <option value="1">Enabled</option>
                            <option value="0">Disabled</option>
                          </select>
                        ) : meta.kind === 'text' ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Leave empty to keep unset"
                            className="w-full rounded-lg border border-brand-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-brand-600 dark:bg-navy-700 dark:text-white"
                            autoFocus
                          />
                        ) : (
                          <>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={editValue}
                              onChange={(e) => {
                                const v = e.target.value;
                                // Allow empty, integers, and decimals
                                if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                  setEditValue(v);
                                }
                              }}
                              className="w-24 rounded-lg border border-brand-300 px-3 py-1.5 text-center text-sm font-semibold focus:border-brand-500 focus:ring-1 focus:ring-brand-500 dark:border-brand-600 dark:bg-navy-700 dark:text-white"
                              autoFocus
                            />
                            {meta.unit && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{meta.unit}</span>
                            )}
                          </>
                        )}
                      </div>
                    ) : meta.kind === 'boolean' ? (
                      <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold ${
                        Number(variable.value)
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-400'
                      }`}>
                        {Number(variable.value) ? 'Enabled' : 'Disabled'}
                      </span>
                    ) : meta.kind === 'text' ? (
                      <span
                        className="block truncate font-mono text-xs text-gray-700 dark:text-gray-300"
                        title={variable.string_value || undefined}
                      >
                        {variable.string_value || <span className="italic text-gray-400">not set</span>}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {Number(variable.value)}{meta.unit ? ` ${meta.unit}` : ''}
                      </span>
                    )}
                  </div>

                  {/* Last Updated */}
                  <div className="col-span-3 text-center">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {variable.updated_at ? new Date(variable.updated_at).toLocaleDateString() : '-'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-center gap-1">
                    {editingId === variable.id ? (
                      <>
                        <button
                          onClick={() => handleSave(variable.id)}
                          disabled={saving}
                          className="rounded-lg p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition disabled:opacity-50"
                          title="Save"
                        >
                          <FiCheck className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700 transition"
                          title="Cancel"
                        >
                          <FiX className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEdit(variable)}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-navy-700 dark:hover:text-brand-400 transition"
                        title="Edit value"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};

export default MasterVariables;
